import { useEffect, useRef } from 'react';

/**
 * Runs `callback` on an interval while enabled, and pauses when the document is
 * hidden. Callers can opt into or out of an immediate first tick.
 */
export function usePolling(
  callback: () => void | Promise<void>,
  intervalMs: number,
  deps: unknown[] = [],
  options: { enabled?: boolean; immediate?: boolean } = {}
) {
  const { enabled = true, immediate = true } = options;
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        try {
          await savedCallback.current();
        } catch {
          /* swallow — next tick will retry */
        }
      }
      if (!cancelled) timer = setTimeout(tick, intervalMs);
    };

    if (immediate) {
      tick();
    } else {
      timer = setTimeout(tick, intervalMs);
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled && immediate) {
        if (timer !== null) clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
